<script>

    import { replace, location } from "svelte-spa-router"
    import StorageService from '../$services/storage.service.js'

    const logoSrc = '/images/TIL.png'
    const logoAlt = 'TIL — Tecnología en Integración Logística'

    const navGroups = [
        {
            label: 'Operación',
            items: [
                { view: 'graphics', path: '/graphics', label: 'Graficas', icon: 'chart-line' },
                { view: 'shipments', path: '/shipments', label: 'Envíos', icon: 'shipping-fast' },
                { view: 'users', path: '/users', label: 'Usuarios', icon: 'users' },
                { view: 'pickups', path: '/pickups', label: 'Recolecciones', icon: 'truck-loading' },
                { view: 'tickets', path: '/tickets', label: 'Tickets', icon: 'ticket-alt' },
            ],
        },
        {
            label: 'Finanzas',
            items: [
                { view: 'credits', path: '/credits', label: 'Créditos', icon: 'coins' },
                { view: 'charges', path: '/charges', label: 'Cargos', icon: 'file-invoice-dollar' },
                { view: 'accounts', path: '/accounts', label: 'Cuentas', icon: 'landmark' },
                { view: 'prepaids', path: '/prepaids', label: 'Prepagos', icon: 'wallet' },
            ],
        },
        {
            label: 'Tarifas',
            items: [
                { view: 'pricings', path: '/pricings', label: 'Precios globales', icon: 'globe' },
                { view: 'fixedprices', path: '/fixedprices', label: 'Precios fijos', icon: 'tag' },
                { view: 'providers', path: '/providers', label: 'Proveedores', icon: 'warehouse' },
            ],
        },
        {
            label: 'TIL',
            items: [
                { view: 'til-pricing', path: '/til-pricing', label: 'Precios TIL', icon: 'dollar-sign' },
                { view: 'coverage', path: '/coverage', label: 'Cobertura TIL', icon: 'map-marked-alt' },
                { view: 'til-trackings', path: '/til-trackings', label: 'Trackings', icon: 'barcode' },
                { view: 'til-seguimiento', path: '/til-seguimiento', label: 'Seguimiento', icon: 'map-marker-alt' },
                { view: 'til-zonas', path: '/til-zonas', label: 'Zonas', icon: 'draw-polygon' },
            ],
        },
        {
            label: 'Integraciones',
            items: [
                { view: 'shopify', path: '/shopify', label: 'Shopify', icon: 'store' },
            ],
        },
        {
            label: 'Sistema',
            items: [
                { view: 'errors', path: '/errors', label: 'Errores', icon: 'bug' },
            ],
        },
    ]

    let sessionUser
    let menuOpen = false

    function closeMenu() {
        menuOpen = false
    }

    function onLogout() {
        closeMenu()
        StorageService.deleteItem('user')
        StorageService.deleteItem('access')
        replace('#/')
    }

    $: {
        void $location
        sessionUser = StorageService.getItem('user')
    }

    function toggleMenu() {
        menuOpen = !menuOpen
    }

    function navigate(view) {
        replace(`#/${view}`)
        closeMenu()
    }

    function onGlobalKeydown(e) {
        if (e.key === 'Escape' && menuOpen) {
            menuOpen = false
        }
    }

    function userInitials(name) {
        const parts = String(name || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
        if (!parts.length) return '?'
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    }
</script>

<svelte:window on:keydown={onGlobalKeydown} />

<nav class="navbar is-hidden-desktop til-mobile-nav" role="navigation" aria-label="Menú principal">
    <div class="navbar-brand">
        <span class="navbar-item til-mobile-nav-brand">
            <img class="til-mobile-nav-logo" src={logoSrc} alt={logoAlt} width="120" height="40" />
        </span>
        <button
            type="button"
            class="navbar-burger"
            class:is-active={menuOpen}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            on:click={toggleMenu}
        >
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
        </button>
    </div>
    <div class="navbar-menu" class:is-active={menuOpen}>
        <aside class="menu">
            {#each navGroups as group}
                <p class="menu-label">{group.label}</p>
                <ul class="menu-list">
                    {#each group.items as item}
                        <li>
                            <a
                                class:is-active={$location === item.path || ($location && $location.startsWith(item.path + '/'))}
                                on:click={() => navigate(item.view)}
                            >
                                <span class="icon is-small">
                                    <i class="fas fa-{item.icon}"></i>
                                </span>
                                <span>{item.label}</span>
                            </a>
                        </li>
                    {/each}
                </ul>
            {/each}
            {#if sessionUser}
                <hr class="navbar-divider" />
                <p class="menu-label">{sessionUser.name || '—'}</p>
                <p class="menu-label is-size-7">{sessionUser.email || ''}</p>
                <button type="button" class="button is-light is-fullwidth" on:click={onLogout}>
                    <span class="icon"><i class="fas fa-sign-out-alt"></i></span>
                    <span>Cerrar sesión</span>
                </button>
            {/if}
        </aside>
    </div>
</nav>

<aside class="til-sidebar is-hidden-touch" aria-label="Menú principal">
    <div class="til-sidebar-panel">
        <header class="til-sidebar-brand">
            <img class="til-sidebar-logo" src={logoSrc} alt={logoAlt} width="200" height="56" />
            <p class="til-sidebar-subtitle">Plataforma admin</p>
        </header>

        <nav class="til-sidebar-nav">
            {#each navGroups as group}
                <div class="til-sidebar-group">
                    <p class="til-sidebar-group-label">{group.label}</p>
                    <ul class="til-sidebar-list">
                        {#each group.items as item}
                            <li>
                                <a
                                    class="til-sidebar-link"
                                    class:is-active={$location === item.path || ($location && $location.startsWith(item.path + '/'))}
                                    href="#/{item.view}"
                                    on:click|preventDefault={() => navigate(item.view)}
                                >
                                    <span class="til-sidebar-link-icon">
                                        <i class="fas fa-{item.icon}"></i>
                                    </span>
                                    <span>{item.label}</span>
                                </a>
                            </li>
                        {/each}
                    </ul>
                </div>
            {/each}
        </nav>

        {#if sessionUser}
            <footer class="til-sidebar-footer">
                <div class="til-sidebar-footer-user">
                    <span class="til-sidebar-footer-avatar" aria-hidden="true">
                        {userInitials(sessionUser.name)}
                    </span>
                    <div class="til-sidebar-footer-meta">
                        <p class="til-sidebar-footer-name">{sessionUser.name || '—'}</p>
                        {#if sessionUser.email}
                            <p class="til-sidebar-footer-email" title={sessionUser.email}>
                                {sessionUser.email}
                            </p>
                        {/if}
                    </div>
                </div>
                <button type="button" class="til-sidebar-logout" on:click={onLogout}>
                    <span class="til-sidebar-logout-icon" aria-hidden="true">
                        <i class="fas fa-sign-out-alt"></i>
                    </span>
                    <span>Cerrar sesión</span>
                </button>
            </footer>
        {/if}
    </div>
</aside>
